import React, { useState } from 'react';
import { ThemeContext } from 'styled-components';
import { useDate } from '@bechtel/client-common';
import { t } from '@lingui/macro';

export const WeekDayXAxis = (props: any) => {
  const theme = React.useContext(ThemeContext);
  const { formatISODate } = useDate();
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={8}
        fill={String(theme.colors.grey['600'])}
        textAnchor="middle"
        style={{
          fontSize: theme.fontSizes['300'],
          fontWeight: Number(theme.fontWeights['600']),
          fontFamily: theme.fontFamilies.graphik,
        }}
      >
        {formatISODate(payload.value, 'EEEE')}
      </text>
      <text
        x={0}
        y={0}
        dy={24}
        fill={String(theme.colors.grey['600'])}
        textAnchor="middle"
        style={{ fontSize: theme.fontSizes['300'] }}
      >
        {formatISODate(payload.value)}
      </text>
    </g>
  );
};

export const InfoPopOver = (props: any) => {
  const theme = React.useContext(ThemeContext);
  const x = props.x + props.width / 2;
  let y = props.y + props.height / 2;
  const x1 = x;
  let y1 = y;
  y = y + 20;
  y1 = y1 - 20;

  //Polygon co-ordinates
  const pts = `${x},${y} ${x + 6},${y - 8} ${x + 12},${y} ${x + 60},${y} ${x + 60},${y + 90} ${x - 48},${y + 90} ${
    x - 48
  },${y} ${x},${y}`;
  const pts1 = `${x1},${y1} ${x1 + 6},${y1 + 8} ${x1 + 12},${y1} ${x1 + 60},${y1} ${x1 + 60},${y1 - 90} ${x1 - 48},${
    y1 - 90
  } ${x1 - 48},${y1} ${x1},${y1}`;

  return (
    <g data-testid="info-popover">
      {props.showInfoPopInBottom ? (
        <>
          <polygon
            fill={theme.colors.white}
            stroke={String(theme.colors.grey['300'])}
            strokeWidth={2}
            points={pts}
            strokeLinejoin="round"
            // filter="drop-shadow(10px 0 5px red)"
          />
          <text
            x={x - 40}
            y={y + 32}
            style={{
              fontSize: theme.fontSizes['800'],
              fontWeight: Number(theme.fontWeights['600']),
              fontFamily: theme.fontFamilies.graphik,
              userSelect: 'none',
            }}
          >
            {props.hours}
          </text>
          <text
            x={x - 38}
            y={y + 48}
            style={{
              fontSize: theme.fontSizes['300'],
              fontWeight: Number(theme.fontWeights['600']),
              fontFamily: theme.fontFamilies.graphikCompact,
              userSelect: 'none',
            }}
          >
            {props.label}
          </text>
          <text
            x={x - 38}
            y={y + 62}
            style={{
              fontSize: theme.fontSizes['300'],
              fill: String(theme.colors.grey['600']),
              userSelect: 'none',
            }}
          >
            {props.subLabel}
          </text>
        </>
      ) : (
        <>
          <polygon fill="#fff" stroke="#c7c7c7" strokeWidth={2} points={pts1} strokeLinejoin="round" />
          <text
            x={x1 - 40}
            y={y1 - 52}
            style={{
              fontSize: theme.fontSizes['800'],
              fontWeight: Number(theme.fontWeights['600']),
              fontFamily: theme.fontFamilies.graphik,
              userSelect: 'none',
            }}
          >
            {props.hours}
          </text>
          <text
            x={x1 - 38}
            y={y1 - 38}
            style={{
              fontSize: theme.fontSizes['300'],
              fontWeight: Number(theme.fontWeights['600']),
              fontFamily: theme.fontFamilies.graphikCompact,
              userSelect: 'none',
            }}
          >
            {props.label}
          </text>
          <text
            x={x1 - 38}
            y={y1 - 22}
            style={{
              fontSize: theme.fontSizes['300'],
              fill: String(theme.colors.grey['600']),
              userSelect: 'none',
            }}
          >
            {props.subLabel}
          </text>
        </>
      )}
    </g>
  );
};

export const TargetIndicatorReferenceArea = ({ targetHours, yAxisMax, ...props }) => {
  const [showTargetInfo, setShowTargetInfo] = useState(true);
  const theme = React.useContext(ThemeContext);
  const x = props.x + props.width / 2;
  const y = props.y + props.height / 2;
  const pts = `${x},${y} ${x + 6},${y + 8} ${x + 12},${y} ${x + 6},${y - 8} ${x},${y}`;
  const showInfoPopInBottom =
    !yAxisMax || yAxisMax == 0 || !targetHours || targetHours == 0 || targetHours <= yAxisMax / 5 ? false : true;

  return (
    <g data-testid="target-indicator-ref-area">
      <polygon
        fill={String(theme.colors.red['600'])}
        stroke={String(theme.colors.red['600'])}
        points={pts}
        onClick={() => setShowTargetInfo(!showTargetInfo)}
        style={{ cursor: 'pointer' }}
      />
      {showTargetInfo && (
        <InfoPopOver
          hours={targetHours}
          label={t`Target`}
          subLabel={t`Hours`}
          showInfoPopInBottom={showInfoPopInBottom}
          {...props}
        />
      )}
    </g>
  );
};

export const ClaimedProgressReferenceArea = ({ cumulativeMilestoneHours, yAxisMax, ...props }) => {
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const theme = React.useContext(ThemeContext);
  const x = props.x + props.width / 2 - 1;
  const y = props.y + props.height / 2;
  const pts = `${x},${y} ${x + 6},${y + 8} ${x + 12},${y} ${x + 6},${y - 8} ${x},${y}`;
  const showInfoPopInBottom =
    !yAxisMax ||
    yAxisMax == 0 ||
    !cumulativeMilestoneHours ||
    cumulativeMilestoneHours == 0 ||
    cumulativeMilestoneHours <= yAxisMax / 5
      ? false
      : true;

  return (
    <g data-testid="claimed-progress-ref-area">
      <polygon
        fill={theme.colors.black}
        stroke={theme.colors.black}
        points={pts}
        style={{ cursor: 'pointer' }}
        onClick={() => setShowInfoPopup(!showInfoPopup)}
      />
      {showInfoPopup && (
        <InfoPopOver
          hours={cumulativeMilestoneHours}
          label={t`Current Progress`}
          subLabel={t`Hours Claimed`}
          showInfoPopInBottom={showInfoPopInBottom}
          {...props}
        />
      )}
    </g>
  );
};
